<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import FilePreviewInput from 'dashboard/components/widgets/FilePreviewInput.vue';

defineProps({
  shortCodeError: { type: Boolean, default: false },
  contentError: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  customRoleOptions: { type: Array, default: () => [] },
  initialFiles: { type: Array, default: () => [] },
});

const emit = defineEmits(['submit', 'cancel', 'shortCodeBlur', 'contentBlur']);

const shortCode = defineModel('shortCode', { type: String, required: true });
const content = defineModel('content', { type: String, required: true });
const contentType = defineModel('contentType', {
  type: String,
  required: true,
});
const selectedRoles = defineModel('selectedRoles', {
  type: Array,
  required: true,
});
const attachmentIds = defineModel('attachmentIds', {
  type: Array,
  required: true,
});

// Saving mid-upload would store the canned response without the file that is still on its way.
const isUploadingAttachment = ref(false);

const { t } = useI18n();
</script>

<template>
  <form class="flex flex-col w-full" @submit.prevent="emit('submit')">
    <div class="w-full">
      <label :class="{ error: shortCodeError }">
        {{ t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
        <input
          v-model="shortCode"
          type="text"
          :placeholder="t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
          @blur="emit('shortCodeBlur')"
        />
      </label>
    </div>

    <div class="w-full">
      <label>
        {{ t('CANNED_MGMT.CONTENT_TYPE.LABEL') }}
        <select v-model="contentType">
          <option value="markdown">
            {{ t('CANNED_MGMT.CONTENT_TYPE.MARKDOWN') }}
          </option>
          <option value="plain_text">
            {{ t('CANNED_MGMT.CONTENT_TYPE.PLAIN_TEXT') }}
          </option>
        </select>
      </label>
      <p class="mb-4 -mt-2 text-xs text-n-slate-11">
        {{ t('CANNED_MGMT.CONTENT_TYPE.HELP') }}
      </p>
    </div>

    <div class="w-full">
      <label :class="{ error: contentError }">
        {{ t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
      </label>
      <div class="editor-wrap">
        <textarea
          v-if="contentType === 'plain_text'"
          v-model="content"
          rows="8"
          :placeholder="t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
          @blur="emit('contentBlur')"
        />
        <WootMessageEditor
          v-else
          v-model="content"
          class="message-editor [&>div]:px-1"
          :class="{ editor_warning: contentError }"
          channel-type="Context::Default"
          enable-variables
          :enable-canned-responses="false"
          :placeholder="t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
          @blur="emit('contentBlur')"
        />
      </div>
    </div>

    <div v-if="customRoleOptions.length" class="w-full">
      <label>
        {{ t('CANNED_MGMT.ROLE_VISIBILITY.LABEL') }}
        <MultiSelect v-model="selectedRoles" :options="customRoleOptions" />
      </label>
      <p class="mt-1 mb-4 text-xs text-n-slate-11">
        {{ t('CANNED_MGMT.ROLE_VISIBILITY.DESCRIPTION') }}
      </p>
    </div>

    <div class="w-full mb-4">
      <label>{{ t('CANNED_MGMT.ATTACHMENT.LABEL') }}</label>
      <FilePreviewInput
        v-model="attachmentIds"
        v-model:uploading="isUploadingAttachment"
        :initial-files="initialFiles"
      />
    </div>

    <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
      <NextButton
        faded
        slate
        type="reset"
        :label="t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
        @click.prevent="emit('cancel')"
      />
      <NextButton
        type="submit"
        :label="t('CANNED_MGMT.ADD.FORM.SUBMIT')"
        :disabled="
          shortCodeError || contentError || isLoading || isUploadingAttachment
        "
        :is-loading="isLoading || isUploadingAttachment"
      />
    </div>
  </form>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}
</style>
